export type TextCleanlinessProblem =
  'leading-or-trailing-space' | 'repeated-space' | 'line-break' | 'control-character';

const PLACEHOLDERS = new Set(['待定', 'tbd', '同上', '暂估', '-', '—', '–', '－']);

function isInvisibleControlCharacter(character: string): boolean {
  const codePoint = character.codePointAt(0);
  if (codePoint === undefined) return false;
  return (
    (codePoint >= 0x00 && codePoint <= 0x08) ||
    codePoint === 0x0b ||
    codePoint === 0x0c ||
    (codePoint >= 0x0e && codePoint <= 0x1f) ||
    (codePoint >= 0x7f && codePoint <= 0x9f) ||
    (codePoint >= 0x200b && codePoint <= 0x200d) ||
    codePoint === 0x2060 ||
    codePoint === 0xfeff
  );
}

function stripInvisibleControlCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => !isInvisibleControlCharacter(character))
    .join('');
}

export function toDisplayText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  return '';
}

export function isBlank(value: unknown): boolean {
  return toDisplayText(value).trim().length === 0;
}

export interface NormalizeTextOptions {
  readonly caseInsensitive?: boolean;
  readonly removeAllWhitespace?: boolean;
}

export function normalizeText(value: unknown, options: NormalizeTextOptions = {}): string {
  let text = stripInvisibleControlCharacters(toDisplayText(value).normalize('NFKC')).trim();
  text = options.removeAllWhitespace ? text.replace(/\s+/gu, '') : text.replace(/\s+/gu, ' ');
  return options.caseInsensitive ? text.toLocaleLowerCase('zh-CN') : text;
}

export function normalizeTextForComparison(value: unknown): string {
  return normalizeText(value, { caseInsensitive: true });
}

export function normalizeCode(value: unknown): string {
  return normalizeText(value, { caseInsensitive: true, removeAllWhitespace: true });
}

export function normalizeUnit(value: unknown): string {
  const compact = normalizeText(value, {
    caseInsensitive: true,
    removeAllWhitespace: true,
  });
  const aliases: Readonly<Record<string, string>> = {
    m2: 'm²',
    平方米: 'm²',
    平米: 'm²',
    m3: 'm³',
    立方米: 'm³',
    立米: 'm³',
    m: 'm',
    米: 'm',
    kg: 'kg',
    千克: 'kg',
    公斤: 'kg',
    t: 't',
    吨: 't',
  };
  return aliases[compact] ?? compact;
}

/** Preserves synonym spelling so BQ012 can distinguish m2, m² and ㎡. */
export function normalizeUnitWriting(value: unknown): string {
  return toDisplayText(value).trim().replace(/\s+/gu, '').toLocaleLowerCase('zh-CN');
}

export function findTextCleanlinessProblems(value: unknown): readonly TextCleanlinessProblem[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  const problems: TextCleanlinessProblem[] = [];
  if (value !== value.trim()) problems.push('leading-or-trailing-space');
  if (/[^\S\r\n]{2,}/u.test(value)) problems.push('repeated-space');
  if (/[\r\n]/u.test(value)) problems.push('line-break');
  if (Array.from(value).some(isInvisibleControlCharacter)) problems.push('control-character');
  return problems;
}

export function isPlaceholderText(value: unknown): boolean {
  const normalized = normalizeText(value, { caseInsensitive: true });
  return PLACEHOLDERS.has(normalized) || normalized.includes('暂估');
}

export function isMeaningfulDescription(value: unknown, minimumLength = 2): boolean {
  const normalized = normalizeText(value, { caseInsensitive: true });
  if (normalized.length === 0) return false;
  const meaningfulCharacters = normalized.replace(/[\p{P}\p{S}\s]/gu, '');
  return meaningfulCharacters.length >= minimumLength;
}
